import { type QueryResult, type OperationVariables } from '@apollo/react-common';
import type { ApolloQueryResult } from 'apollo-client';
import {
	type QueryHookOptions,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- `@confluence/apollo-relay-interop` package is the only one where such imports are allowed
	useQuery,
} from '@apollo/react-hooks';
import { type GraphQLTaggedNode, useRelayEnvironment } from 'react-relay';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { type ConcreteRequest, createOperationDescriptor, getRequest } from 'relay-runtime';
import { type DefinitionNode } from 'graphql';

import UFOInteractionContext from '@atlaskit/react-ufo/interaction-context';

import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { isRelayEnabled } from './isRelayEnabled.ts';
import { fetchQuery } from './fetchQuery.ts';
import { mapFetchPolicyForQuery, type QueryInteropFetchPolicy } from './mapFetchPolicyForQuery.ts';
import { useMemoObj } from './useMemoObj.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import type { ApolloInteropMutableHelper } from './ApolloInteropMutableHelper.ts';
import { NetworkStatus } from './NetworkStatus.ts';
import { useApolloQueryCacheBridgeSync } from './cacheBridge/useApolloQueryCacheBridgeSync.ts';
import { syncQueryCacheBridge } from './cacheBridge/queryCacheBridgeSync.ts';

export type RefetchSupportedType<TData, TVariables> = (
	vars?: TVariables,
) => Promise<Pick<ApolloQueryResult<TData>, 'data'> & { error?: ApolloErrorInterop }>;

/**
 * Options for the fetchMore function, mirroring Apollo v2's FetchMoreOptions.
 * @see https://www.apollographql.com/docs/react/v2/data/pagination/
 */
export type FetchMoreOptions<TData, TVariables> = {
	/**
	 * Variables to use for the fetchMore query. These are merged with the original variables.
	 */
	variables?: Partial<TVariables>;
	/**
	 * Function to merge the previous query result with the fetchMore result.
	 * This is required and is where you implement your pagination merge logic.
	 * Note: Uses ApolloInteropMutableHelper to support mutable updates on Relay's readonly types.
	 */
	updateQuery: (
		previousResult: ApolloInteropMutableHelper<TData>,
		options: { fetchMoreResult?: TData; variables?: TVariables },
	) => TData;
};

export type FetchMoreType<TData, TVariables> = (
	options: FetchMoreOptions<TData, TVariables>,
) => Promise<ApolloQueryResult<TData>>;

export type UseQueryInteropSupportedResult<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Pick<
	QueryResult<TData, TVariables>,
	'data' | 'loading' | 'variables' | 'startPolling' | 'stopPolling'
> & {
	refetch: RefetchSupportedType<TData, TVariables>;
	fetchMore: FetchMoreType<TData, TVariables>;
	error?: ApolloErrorInterop;
	/**
	 * The current network status of the query.
	 * @see NetworkStatus enum for possible values
	 */
	networkStatus: NetworkStatus;
};

export type UseQueryInteropSupportedHookOptions<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Pick<
	QueryHookOptions<TData, TVariables>,
	| 'variables'
	| 'skip'
	| 'onError'
	| 'errorPolicy'
	| 'pollInterval'
	| 'notifyOnNetworkStatusChange'
	| 'onError'
	| 'onCompleted'
	| 'context'
> & {
	/**
	 * Fetch policy for the initial fetch. Apollo-only values
	 * ('cache-and-network', 'standby') are mapped to the closest Relay equivalent.
	 */
	fetchPolicy?: QueryInteropFetchPolicy;
	/** Fetch policy used by `refetch` / `fetchMore`. Defaults to `fetchPolicy`. */
	nextFetchPolicy?: QueryInteropFetchPolicy;
	/**
	 * Ensures that the interop in Apollo build variant uses the AGG client
	 */
	interopApolloAggClient?: boolean;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export type UseQueryInteropSupportedArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> =
	| [GraphQLTaggedNode]
	| [GraphQLTaggedNode, UseQueryInteropSupportedHookOptions<TData, TVariables>];

export function useQueryInterop<TData, TVariables extends Record<string, any> = OperationVariables>(
	...[query, params]: UseQueryInteropSupportedArgs<TData, TVariables>
): UseQueryInteropSupportedResult<TData, TVariables> {
	if (isRelayEnabled(params?.__callerPackage__)) {
		// eslint-disable-next-line react-hooks/rules-of-hooks -- Apollo/Relay route is branch-stable for a given caller package.
		return useQueryInteropRelay<TData, TVariables>(query, params);
	}

	// eslint-disable-next-line react-hooks/rules-of-hooks -- Apollo/Relay route is branch-stable for a given caller package.
	return useQueryInteropApollo<TData, TVariables>(query, params);
}

function useQueryInteropApollo<TData, TVariables extends Record<string, any> = OperationVariables>(
	query: GraphQLTaggedNode,
	params?: UseQueryInteropSupportedHookOptions<TData, TVariables>,
): UseQueryInteropSupportedResult<TData, TVariables> {
	const { interopApolloAggClient, nextFetchPolicy, ...apolloOptions } = params ?? {};

	const documentNode = toDocumentNode(query);
	const {
		getVariables: getCacheBridgeVariables,
		isEnabled: isCacheBridgeEnabled,
		onCompleted: onApolloCompletedWithCacheBridge,
		syncObservedResult,
	} = useApolloQueryCacheBridgeSync<TData, TVariables>({
		query,
		variables: apolloOptions.variables,
		fetchPolicy: apolloOptions.fetchPolicy,
		useAggClient: interopApolloAggClient,
		onCompleted: apolloOptions.onCompleted,
		callerPackage: params?.__callerPackage__,
	});
	const cacheBridgeOptionVariables = getCacheBridgeVariables();
	const onApolloCompletedWithCacheBridgeVariables = useCallback(
		(completedData: TData) =>
			onApolloCompletedWithCacheBridge(completedData, cacheBridgeOptionVariables),
		[cacheBridgeOptionVariables, onApolloCompletedWithCacheBridge],
	);
	const onApolloCompleted = isCacheBridgeEnabled
		? onApolloCompletedWithCacheBridgeVariables
		: apolloOptions.onCompleted;

	const apolloResult = useQuery<TData, TVariables>(
		...[
			documentNode,
			{
				...apolloOptions,
				...(onApolloCompleted ? { onCompleted: onApolloCompleted } : {}),
				...(nextFetchPolicy ? { nextFetchPolicy } : {}),
				...(interopApolloAggClient
					? {
							client:
								window['GLOBAL_APOLLO_AGG_CLIENT'] ?? (window as any)['MOCK_GATEWAY_APOLLO_CLIENT'],
						}
					: {}),
				context: {
					...apolloOptions?.context,
					requestId: (query as ConcreteRequest).params?.id,
				},
			},
		],
	);
	const apolloData = apolloResult.data;
	const apolloError = apolloResult.error;
	const apolloSkip = apolloOptions.skip;
	const apolloVariables = getCacheBridgeVariables(apolloResult.variables);

	useEffect(() => {
		if (!isCacheBridgeEnabled) {
			return;
		}

		syncObservedResult({
			skip: apolloSkip,
			variables: apolloVariables,
			data: apolloData,
			error: apolloError,
		});
	}, [
		apolloData,
		apolloError,
		apolloSkip,
		apolloVariables,
		isCacheBridgeEnabled,
		syncObservedResult,
	]);

	// Note: UFO holds for the Apollo path are handled automatically by the
	// parcel-resolver-spa-module-replacements which replaces @apollo/react-hooks
	// with @atlassian/ufo-apollo-log/react-hooks at build time.

	// Hide 'client' on the apolloResult object so callers cannot access the Apollo
	// client instance directly via the hook result. We use Object.defineProperty
	// instead of spreading to avoid creating a new object reference on every render,
	// which would cause components using componentDidUpdate to loop infinitely.
	Object.defineProperty(apolloResult, 'client', {
		get() {
			return undefined;
		},
		enumerable: false,
		configurable: true,
	});
	return apolloResult as unknown as UseQueryInteropSupportedResult<TData, TVariables>;
}

function useQueryInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	query: GraphQLTaggedNode,
	options?: UseQueryInteropSupportedHookOptions<TData, TVariables>,
): UseQueryInteropSupportedResult<TData, TVariables> {
	const environment = useRelayEnvironment();

	// Extract variables and memoize them to prevent unnecessary re-renders
	const variables = useMemoObj<TVariables>(options?.variables, options?.__callerPackage__);
	const context = useMemoObj(options?.context ?? {}, options?.__callerPackage__);

	if (!('params' in query)) {
		const operationName =
			'definitions' in query
				? ((query as { definitions: readonly DefinitionNode[] }).definitions?.find(
						(d): d is DefinitionNode & { name?: { value: string } } =>
							d.kind === 'OperationDefinition' && 'name' in d,
					)?.name?.value ?? 'unknown')
				: 'unknown';
		throw new Error(
			`[Relay Migration] unsupported tagged node provided for operation "${operationName}". Please make sure you generated Relay artifacts for your operation`,
		);
	}

	const skip = options?.skip || false;
	const notifyOnNetworkStatusChange = options?.notifyOnNetworkStatusChange || false;
	const pollInterval = options?.pollInterval ?? 0;

	// Only forward an explicit policy; leaving it undefined preserves Relay's
	// default ('network-only'). This intentionally differs from the imperative
	// `queryInteropRelay`, which defaults to 'store-or-network'.
	const initialFetchPolicy = options?.fetchPolicy
		? mapFetchPolicyForQuery(options.fetchPolicy)
		: undefined;
	const subsequentFetchPolicy = options?.nextFetchPolicy
		? mapFetchPolicyForQuery(options.nextFetchPolicy)
		: initialFetchPolicy;
	// Polling defaults to network-only to match Apollo.
	const pollFetchPolicy = subsequentFetchPolicy ?? 'network-only';
	const cacheBridgeFetchPolicy = options?.fetchPolicy;
	const cacheBridgeSubsequentFetchPolicy = options?.nextFetchPolicy ?? cacheBridgeFetchPolicy;
	const interopApolloAggClient = options?.interopApolloAggClient;
	const operationName = (query as ConcreteRequest).params?.name ?? 'unnamed';
	const operation = useMemo(
		() => (skip ? null : createOperationDescriptor(getRequest(query), variables)),
		[query, skip, variables],
	);
	const initialSnapshot = operation ? environment.lookup(operation.fragment) : undefined;
	const hasCompleteInitialSnapshot = Boolean(
		operation && initialSnapshot && !initialSnapshot.isMissingData,
	);
	const initialData = hasCompleteInitialSnapshot ? (initialSnapshot?.data as TData) : undefined;
	const initialNetworkStatus = skip
		? NetworkStatus.ready
		: hasCompleteInitialSnapshot
			? NetworkStatus.ready
			: NetworkStatus.loading;

	// Track whether the initial fetch has completed so we can switch to nextFetchPolicy
	const hasCompletedInitialFetch = useRef(false);

	// State to manage the query result
	const [data, setData] = useState<TData | undefined>(initialData);
	const [loading, setLoading] = useState(initialNetworkStatus === NetworkStatus.loading);
	const [error, setError] = useState<any>(undefined);
	const [networkStatus, setNetworkStatus] = useState<NetworkStatus>(initialNetworkStatus);
	const onError = useStableCallback(options?.onError || function noop() {});
	const onCompleted = useStableCallback(options?.onCompleted || function noop() {});
	const mirrorRelayResultToApollo = useCallback(
		(
			relayResult: TData | undefined,
			resultVariables: TVariables = variables,
			fetchPolicy: QueryInteropFetchPolicy | undefined = cacheBridgeFetchPolicy,
		) => {
			// Relay state has already been updated; mirror the successful result
			// into Apollo as secondary cache bridge work.
			syncQueryCacheBridge({
				source: 'relay',
				query,
				variables: resultVariables,
				data: relayResult,
				fetchPolicy,
				useAggClient: interopApolloAggClient,
			});
		},
		[cacheBridgeFetchPolicy, interopApolloAggClient, query, variables],
	);

	// Refs keep startPolling/stopPolling identity-stable across renders.
	const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const latestEnvRef = useRef(environment);
	const latestQueryRef = useRef(query);
	const latestVarsRef = useRef(variables);
	const latestContextRef = useRef(context);
	const isMountedRef = useRef(true);
	// Once fetchMore merges pages in JS, the store only holds a single page, so live
	// store updates must not overwrite the displayed (merged) list.
	const hasMergedPagesRef = useRef(false);

	useEffect(() => {
		latestEnvRef.current = environment;
		latestQueryRef.current = query;
		latestVarsRef.current = variables;
		latestContextRef.current = context;
	}, [environment, query, variables, context]);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const stopPolling = useCallback(() => {
		if (pollTimerRef.current !== null) {
			clearInterval(pollTimerRef.current);
			pollTimerRef.current = null;
		}
	}, []);

	const runPollTick = useCallback(() => {
		// When notifyOnNetworkStatusChange is opted-in, mirror Apollo: flip
		// networkStatus to `poll` and `loading` to true during the in-flight tick.
		if (notifyOnNetworkStatusChange) {
			setNetworkStatus(NetworkStatus.poll);
			setLoading(true);
		}
		fetchQuery<{ response: TData; variables: TVariables }>(
			latestEnvRef.current,
			latestQueryRef.current,
			latestVarsRef.current,
			{
				fetchPolicy: pollFetchPolicy,
				networkCacheConfig: { metadata: latestContextRef.current },
			},
		)
			.toPromise()
			.then((result) => {
				if (!isMountedRef.current) return;
				if (notifyOnNetworkStatusChange) {
					setNetworkStatus(NetworkStatus.ready);
					setLoading(false);
				}
				// Guard against empty completions: don't wipe data or invoke
				// onCompleted with undefined.
				if (result !== undefined) {
					mirrorRelayResultToApollo(
						result as TData,
						latestVarsRef.current,
						cacheBridgeSubsequentFetchPolicy,
					);
					setData(result as TData);
					setError(undefined);
					onCompleted(result as TData);
				}
			})
			.catch((err) => {
				if (!isMountedRef.current) return;
				setError(err);
				if (notifyOnNetworkStatusChange) {
					setNetworkStatus(NetworkStatus.ready);
					setLoading(false);
				}
				onError(err);
				// Match Apollo: keep polling on errors.
			});
	}, [
		mirrorRelayResultToApollo,
		cacheBridgeSubsequentFetchPolicy,
		notifyOnNetworkStatusChange,
		onCompleted,
		onError,
		pollFetchPolicy,
	]);

	const startPolling = useCallback(
		(interval: number) => {
			// No-op for non-positive intervals (Apollo parity).
			if (typeof interval !== 'number' || interval <= 0) {
				return;
			}
			// Skip on the server — guard before any destructive timer ops.
			if (typeof window === 'undefined') {
				return;
			}
			// Clear any existing timer so the new interval takes effect.
			if (pollTimerRef.current !== null) {
				clearInterval(pollTimerRef.current);
				pollTimerRef.current = null;
			}
			pollTimerRef.current = setInterval(runPollTick, interval);
		},
		[runPollTick],
	);

	// Auto-start when pollInterval is set and not skipped.
	useEffect(() => {
		if (skip || !pollInterval || pollInterval <= 0) {
			stopPolling();
			return;
		}
		startPolling(pollInterval);
		return stopPolling;
	}, [skip, pollInterval, startPolling, stopPolling]);

	// Hold the UFO interaction while the Relay query is loading.
	// This mirrors what @atlassian/ufo-apollo-log does for Apollo queries via
	// the parcel-resolver-spa-module-replacements build-time replacement.
	const ufoContext = useContext(UFOInteractionContext);

	useEffect(() => {
		if (loading && networkStatus !== NetworkStatus.poll) {
			return ufoContext?.hold(operationName);
		}
	}, [loading, networkStatus, operationName, ufoContext]);

	// Use fetchQuery to get data
	useEffect(() => {
		// Treat each dep-change re-run as a fresh initial fetch.
		// `refetch`/`fetchMore` use `subsequentFetchPolicy` separately.
		hasCompletedInitialFetch.current = false;
		hasMergedPagesRef.current = false;

		if (skip) {
			setLoading(false);
			setData(undefined);
			setError(undefined);
			setNetworkStatus(NetworkStatus.ready);
			return;
		}
		if (!operation) {
			setLoading(false);
			setData(undefined);
			setError(undefined);
			setNetworkStatus(NetworkStatus.ready);
			return;
		}

		setLoading(true);
		setError(undefined);
		setNetworkStatus(NetworkStatus.loading);

		// Subscribe to the Relay store so cache writes (e.g. writeQueryInterop/commitUpdate
		// after a mutation) re-render consumers, matching Apollo's cache-watching useQuery.
		const retainDisposable = environment.retain(operation);
		const storeSubscription = environment.subscribe(
			environment.lookup(operation.fragment),
			(snapshot) => {
				// Only after the initial fetch, and never once fetchMore has merged pages in JS
				// (the store holds a single page and would clobber the merged list).
				if (
					hasCompletedInitialFetch.current &&
					!hasMergedPagesRef.current &&
					!snapshot.isMissingData &&
					isMountedRef.current
				) {
					setData(snapshot.data as TData);
				}
			},
		);

		const subscription = fetchQuery<{ response: TData; variables: TVariables }>(
			environment,
			query,
			variables,
			{
				fetchPolicy: initialFetchPolicy,
				networkCacheConfig: { metadata: context },
			},
		).subscribe({
			next: (result) => {
				hasCompletedInitialFetch.current = true;
				mirrorRelayResultToApollo(result as TData);
				setData(result);
				setLoading(false);
				setNetworkStatus(NetworkStatus.ready);
				onCompleted(result as TData);
			},
			error: (err: any) => {
				hasCompletedInitialFetch.current = true;
				setError(err);
				setLoading(false);
				// Note: Apollo v2 keeps networkStatus at ready on errors, only sets the error field
				setNetworkStatus(NetworkStatus.ready);
				onError(err);
			},
		});

		return () => {
			subscription.unsubscribe();
			storeSubscription.dispose();
			retainDisposable.dispose();
		};
	}, [
		environment,
		query,
		variables,
		skip,
		onError,
		onCompleted,
		context,
		initialFetchPolicy,
		mirrorRelayResultToApollo,
		operation,
	]);

	// Create a refetch function for Apollo compatibility
	const refetch: RefetchSupportedType<TData, TVariables> = useCallback(
		(newVariables?: TVariables) => {
			const varsToUse = newVariables || variables;
			setLoading(true);
			setError(undefined);
			setNetworkStatus(NetworkStatus.refetch);

			return fetchQuery<{ response: TData; variables: TVariables }>(environment, query, varsToUse, {
				fetchPolicy: subsequentFetchPolicy,
				networkCacheConfig: { metadata: context },
			})
				.toPromise()
				.then((result) => {
					mirrorRelayResultToApollo(result as TData, varsToUse, cacheBridgeSubsequentFetchPolicy);
					setData(result);
					setLoading(false);
					setNetworkStatus(NetworkStatus.ready);
					return {
						data: result as TData,
						loading: false,
						networkStatus: NetworkStatus.ready,
						stale: false,
					};
				})
				.catch((err) => {
					setError(err);
					setLoading(false);
					setNetworkStatus(NetworkStatus.error);
					throw err;
				});
		},
		[
			environment,
			query,
			variables,
			context,
			subsequentFetchPolicy,
			mirrorRelayResultToApollo,
			cacheBridgeSubsequentFetchPolicy,
		],
	);

	// Create a fetchMore function for pagination support
	const fetchMore: FetchMoreType<TData, TVariables> = useCallback(
		(fetchMoreOpts: FetchMoreOptions<TData, TVariables>) => {
			// Merge the new variables with existing variables
			const fetchMoreVariables = {
				...variables,
				...fetchMoreOpts.variables,
			} as TVariables;

			// Only set loading/networkStatus if notifyOnNetworkStatusChange is enabled
			// This matches Apollo v2 behavior where loading stays false during fetchMore
			// unless explicitly opting in to network status notifications
			if (notifyOnNetworkStatusChange) {
				setLoading(true);
				setNetworkStatus(NetworkStatus.fetchMore);
			}

			return fetchQuery<{ response: TData; variables: TVariables }>(
				environment,
				query,
				fetchMoreVariables,
				{
					fetchPolicy: subsequentFetchPolicy,
					networkCacheConfig: { metadata: context },
				},
			)
				.toPromise()
				.then((fetchMoreResult) => {
					// Use updateQuery to merge the new results with existing data
					// Pass empty object if data is undefined (matches Apollo v2 behavior)
					const mergedData = fetchMoreOpts.updateQuery(data ?? ({} as TData), {
						fetchMoreResult: fetchMoreResult as TData,
						variables: fetchMoreVariables,
					});
					// Merged pages live only in React state, not the store; stop store-driven
					// updates so they don't revert the list to a single page.
					hasMergedPagesRef.current = true;
					mirrorRelayResultToApollo(mergedData, variables, cacheBridgeSubsequentFetchPolicy);
					setData(mergedData);
					if (notifyOnNetworkStatusChange) {
						setLoading(false);
						setNetworkStatus(NetworkStatus.ready);
					}
					return {
						data: mergedData,
						loading: false,
						networkStatus: NetworkStatus.ready,
						stale: false,
					};
				})
				.catch((err) => {
					if (notifyOnNetworkStatusChange) {
						setLoading(false);
						setNetworkStatus(NetworkStatus.ready);
					}
					throw err;
				});
		},
		[
			environment,
			query,
			variables,
			context,
			data,
			mirrorRelayResultToApollo,
			notifyOnNetworkStatusChange,
			cacheBridgeSubsequentFetchPolicy,
			subsequentFetchPolicy,
		],
	);

	const result = {
		data,
		loading,
		error,
		refetch,
		fetchMore,
		variables,
		networkStatus,
		startPolling,
		stopPolling,
	};
	return result as UseQueryInteropSupportedResult<TData, TVariables>;
}
